// A marca na tela — a da plataforma ou a da revenda.
//
// Até 31/10/2026 isto era só o logo da CDW. Agora a revenda pode pôr nome e
// logo dela (api/interno/web/marca.go), e o cliente final de uma revenda não
// pode ver "CDW Mail" em lugar nenhum: nem no logo, nem na aba, nem no ícone.
//
// O logo da PLATAFORMA continua vindo no pacote (logo.jpg): é o caso de quase
// todo mundo, e buscar pela rede o que já está aqui seria uma ida à toa. O da
// REVENDA vem pela API, com a versão no endereço — cache longo e troca na
// hora.
//
// Sobre o logo.jpg: é JPEG, embora a origem o servisse como .png, e NÃO tem
// transparência — só assenta sobre superfície branca. Se um dia houver tema
// escuro, ele precisa virar PNG com canal alfa.

import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import * as marcaAPI from "../api/marca";
import type { Marca as DadosDaMarca } from "../api/marca";
import { useSessao } from "../sessao/contexto";
import logoDaPlataforma from "./logo.jpg";

const DEZ_MINUTOS = 10 * 60_000;

/**
 * A marca a desenhar agora.
 *
 * Antes do login, a do ENDEREÇO (`webmail.cliente.com.br` → revenda do
 * cliente). Depois, a da CONTA. Enquanto a da conta carrega, vale a do
 * endereço — que quase sempre é a mesma, e evita o logo piscar.
 *
 * `undefined` = ainda não se sabe. A tela reserva o espaço e não desenha
 * nada: mostrar a da plataforma "enquanto isso" seria mostrar CDW, por um
 * instante, ao cliente de uma revenda — justamente o que esta tela evita.
 */
export function useMarca(): DadosDaMarca | undefined {
  const { sessao } = useSessao();
  const doEndereco = useQuery({
    queryKey: ["marca", "endereco"],
    queryFn: marcaAPI.doEndereco,
    staleTime: DEZ_MINUTOS,
  });
  const daConta = useQuery({
    queryKey: ["marca", "conta"],
    queryFn: marcaAPI.daConta,
    enabled: !!sessao,
    staleTime: DEZ_MINUTOS,
  });
  if (sessao) return daConta.data ?? doEndereco.data;
  return doEndereco.data;
}

/**
 * O desenho, separado da busca: a tela de edição da marca usa o MESMO
 * componente para a prévia, e por isso a prévia é o que vai aparecer — não
 * uma imitação que pode divergir.
 */
export function DesenhoDaMarca({
  marca,
  altura,
  className = "",
}: {
  marca: { nome: string; logo_url?: string | null; padrao: boolean } | undefined;
  altura: number;
  className?: string;
}) {
  if (!marca) {
    // Espaço reservado, do tamanho aproximado do logo: sem ele a barra
    // inteira pula quando a marca chega.
    return <span aria-hidden className={className} style={{ height: altura, width: altura * 4 }} />;
  }
  const src = marca.padrao ? logoDaPlataforma : marca.logo_url;
  if (src) {
    return (
      <img
        src={src}
        alt={marca.nome}
        height={altura}
        className={`object-contain object-left ${className}`}
        // O TETO DE LARGURA é da revenda: o da plataforma tem proporção
        // conhecida (800×183), um logo de revenda pode ter qualquer uma, e
        // um logo de 2000×100 a 30px de altura empurraria a busca para fora
        // da barra.
        style={{ height: altura, width: "auto", maxWidth: altura * 6 }}
        decoding="async"
      />
    );
  }
  // Revenda sem logo: o nome, escrito.
  return (
    <span
      className={`truncate font-semibold tracking-tight text-tinta ${className}`}
      style={{ fontSize: Math.round(altura * 0.62), lineHeight: `${altura}px`, maxWidth: altura * 7 }}
    >
      {marca.nome}
    </span>
  );
}

export function Marca({ altura = 30, className }: { altura?: number; className?: string }) {
  return <DesenhoDaMarca marca={useMarca()} altura={altura} className={className} />;
}

// ---------------------------------------------------------------------------
// A aba do navegador: título e ícone
// ---------------------------------------------------------------------------

function definirLink(rel: string, href: string) {
  let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement("link");
    link.rel = rel;
    document.head.appendChild(link);
  }
  link.type = "image/png";
  link.href = href;
}

/**
 * Ícone com a INICIAL da marca, desenhado na hora.
 *
 * O ícone da plataforma é o logo da CDW, e a revenda não manda ícone — só
 * logo, que é largo e vira um borrão em 16 px. A inicial sobre a cor da
 * plataforma é legível no tamanho de uma aba e não diz CDW.
 *
 * Canvas e não SVG: o Safari não usa SVG como ícone de aba.
 */
export function iconeDaInicial(nome: string): string | null {
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = 128;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null; // ambiente sem canvas (testes): fica sem ícone
  ctx.fillStyle = "#0f766e";
  ctx.beginPath();
  // roundRect é recente (Safari 16, Firefox 112); sem ele, quadrado.
  if (typeof ctx.roundRect === "function") ctx.roundRect(0, 0, 128, 128, 28);
  else ctx.rect(0, 0, 128, 128);
  ctx.fill();
  // Array.from separa por caractere, não por unidade UTF-16: "É" e emoji
  // saem inteiros.
  const inicial = (Array.from(nome.trim())[0] ?? "?").toLocaleUpperCase("pt-BR");
  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 84px system-ui, -apple-system, 'Segoe UI', sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(inicial, 64, 70);
  return canvas.toDataURL("image/png");
}

/**
 * Põe a marca na aba. Montado uma vez, na raiz.
 *
 * O index.html sai NEUTRO de propósito — título "Webmail" e sem ícone —
 * para o cliente de uma revenda não ver "CDW Mail" na aba enquanto o
 * JavaScript carrega. Quem decide o que entra é isto.
 */
export function AplicarMarca() {
  const marca = useMarca();
  const nome = marca?.nome;
  const padrao = marca?.padrao;
  useEffect(() => {
    if (nome === undefined) return;
    document.title = nome;
    if (padrao) {
      definirLink("icon", "/favicon.png");
      definirLink("apple-touch-icon", "/apple-touch-icon.png");
      return;
    }
    const icone = iconeDaInicial(nome);
    if (icone) {
      definirLink("icon", icone);
      definirLink("apple-touch-icon", icone);
    }
  }, [nome, padrao]);
  return null;
}
